import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import { Mutation } from 'react-apollo';
import { push } from 'react-router-redux';
import {
  Form,
  Field,
  FieldArray,
  reduxForm,
} from 'redux-form';
import moment from 'moment';
import deleteIcon from '../../../../images/delete.svg';
import dotsMenu from '../../../../images/pdf-svgrepo-com.svg';
import BlankState from '../../../BlankState';
import handleToastError from '../../../../helpers/handleToastError';
import { adminDeleteApplicationMutation, adminSiteQuery } from '../../../../apollo';

const renderApplications = ({
  fields,
  siteId,
  dispatch,
  enums,
}) => (
  <Fragment>
    {
      fields.map((application, index) => {
        const {
          applicationYear,
          type,
          submittedAt,
          submittedBy,
          status,
          applicationKidsCafeId,
        } = fields.get(index);

        return (
          <Fragment key={applicationKidsCafeId}>
            <div className="table-row-3 applications w-clearfix">
              <div
                onClick={() => dispatch(push(`/kids-cafe/application/step-1/${applicationKidsCafeId}`))}
                className="app-table-col year view-application"
              >
                <div>{type === 'SUMMER' ? `Summer ${parseInt(applicationYear)+1}` : `School Year ${applicationYear}`}</div>
              </div>
              <div className="app-table-col sub-date">
                <div>{submittedAt ? moment.utc(submittedAt).format('M-D-YY') : 'N/A'}</div>
              </div>
              <div className="app-table-col sub-by">
                <div className="text-force-wrap">{submittedBy}</div>
              </div>
              <div className="app-table-col school-stat">
                <div className="form-block-6 w-form">
                  <Field
                    component="select"
                    className="select-dropdown w-select"
                    name={`${application}.status`}
                    disabled={status === 'IN_PROGRESS'}
                  >
                    {
                      enums.applicationStatuses.options.map(({ value, label }) => (
                        <option
                          disabled={value === 'IN_PROGRESS' || value === 'PENDING'}
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      ))
                    }
                  </Field>
                </div>
              </div>
              <div className="app-table-col actions tablet-wrap">
                <div className="crud-action-icon-wrapper-2 w-inline-block" onClick={() => dispatch(push(`/kids-cafe/print/${applicationKidsCafeId}/${type === 'SUMMER' ? `Summer ${applicationYear}` : `School Year ${applicationYear}`}`))}>
                  <img className="pointer" src={dotsMenu} alt="Print Application" title="Print Application" />
                </div>
                <Mutation
                  mutation={adminDeleteApplicationMutation}
                  variables={{ input: { applicationKidsCafeId } }}
                  refetchQueries={[
                    { query: adminSiteQuery, variables: { siteId } },
                  ]}
                  onError={mutationError => handleToastError(mutationError)}
                  onCompleted={() => toast('Delete Successful')}
                >
                  {(adminDeleteApplication) => {
                    const confirmDelete = () => {
                      confirmAlert({
                        message: 'Are you sure?',
                        buttons: [
                          {
                            label: 'Cancel',
                          },
                          {
                            label: 'Delete',
                            onClick: adminDeleteApplication,
                          },
                        ],
                      });
                    };

                    return (
                      <div className="crud-action-icon-wrapper-2 w-inline-block" onClick={confirmDelete}>
                        <img className="pointer" src={deleteIcon} alt="Delete Application" title="Delete Application" />
                      </div>
                    );
                  }}
                </Mutation>
              </div>
            </div>
          </Fragment>
        );
      })
    }
  </Fragment>
);

renderApplications.propTypes = {
  fields: PropTypes.object.isRequired,
  siteId: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  enums: PropTypes.object.isRequired,
};

const ApplicationForm = reduxForm({
  form: 'siteApplications',
  enableReinitialize: true,
  destroyOnUnmount: true,
  forceUnregisterOnUnmount: true,
})(({
  handleSubmit,
  submitting,
  dirty,
  update,
  reset,
  dispatch,
  enums,
  initialValues: {
    siteId,
    applicationsKidsCafe,
  },
}) => {
  const submitAndReset = (data) => {
    const applicationsNormalized = data.applicationsKidsCafe.map(({
      applicationKidsCafeId,
      status,
    }) => ({
      applicationKidsCafeId,
      status,
    }));

    const normalizedData = {
      siteId,
      applicationsKidsCafe: applicationsNormalized,
    };

    return (
      update(normalizedData).then(() => (
        dispatch(reset('siteApplications'))
      ))
    );
  };

  return (
    <Form onSubmit={handleSubmit(submitAndReset.bind(this))}>
      <div className="applications-table-row-2 w-clearfix">
        <span className="app-table-col year w-inline-block">
          <div className="table-data-4 year">Application Year</div>
        </span>
        <span className="app-table-col sub-date w-inline-block">
          <div className="table-data-4 status">Submission</div>
        </span>
        <span className="app-table-col sub-by w-inline-block">
          <div className="table-data-4 status">Submitted By</div>
        </span>
        <span className="app-table-col school-stat w-inline-block">
          <div className="table-data-4 status">Status</div>
        </span>
      </div>
      {
        applicationsKidsCafe.length ? (
          <Fragment>
            <FieldArray
              name="applicationsKidsCafe"
              component={renderApplications}
              props={{ enums, siteId, dispatch }}
            />
            <div className="btn-padding">
              <input
                type="submit"
                value={submitting ? 'Please wait...' : 'Save Changes'}
                disabled={submitting || !dirty}
                className="btn-solid-green w-button"
              />
            </div>
          </Fragment>
        ) : <BlankState title="No Results" />
      }
    </Form>
  );
});

const mapStateToProps = (reduxState, props) => {
  const { site } = props;
  const { siteId, applicationsKidsCafe } = site;

  const applicationsData = applicationsKidsCafe.map(({
    applicationKidsCafeId,
    submittedAt,
    submittedBy,
    status,
    applicationYear,
    type,
  }) => ({
    applicationKidsCafeId,
    submittedAt,
    submittedBy,
    status,
    applicationYear,
    type,
  }));

  return {
    initialValues: {
      siteId,
      applicationsKidsCafe: applicationsData,
    },
  };
};

ApplicationForm.propTypes = {
  enums: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(ApplicationForm);
